/**
 * 역할·권한 어드민 훅 (TanStack Query) — roles + permissions(3단 RBAC)
 *
 * 계약 §1.3:
 *   GET    /roles                                역할 목록
 *   POST   /roles   PUT /roles/{id}   DELETE /roles/{id}   역할 CRUD
 *   GET    /roles/{id}/permissions   PUT /roles/{id}/permissions   권한(메뉴/업무/필드 3단)
 *
 * 동적 메타모델 원칙(절대):
 * - 권한 대상(메뉴/업무유형/필드)은 메타데이터 식별자(targetType+targetId)로 표현.
 * - 하드코딩 권한 분기 금지. isSystem 역할은 편집 제한 표기(서버 권위 검증).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { Permission, Role } from "@eqms/shared-types";

// ---------------------------------------------------------------------------
// 쿼리 키 (회사 단위 마스터 — 워크스페이스 비종속)
// ---------------------------------------------------------------------------

export const roleKeys = {
  all: () => ["roles"] as const,
  permissions: (roleId: string) => ["roles", roleId, "permissions"] as const,
};

/** 사용자(담당자)별 권한 쿼리 키 — 설정>메뉴 권한은 담당자 × 워크스페이스 단위 저장. */
export const userPermKeys = {
  permissions: (userId: string, workspaceSlug?: string) =>
    ["users", userId, "permissions", workspaceSlug ?? ""] as const,
};

// ---------------------------------------------------------------------------
// 응답/요청 DTO
// ---------------------------------------------------------------------------

/** 권한 대상 유형 (3단 RBAC) — 시스템 고정값 */
export const PermissionTarget = {
  /** 메뉴(모듈/설정 노출) */
  MENU: "menu",
  /** 업무(업무유형 단위) */
  WORKITEM: "workitem",
  /** 필드(field_def 단위) */
  FIELD: "field",
} as const;
export type PermissionTarget = (typeof PermissionTarget)[keyof typeof PermissionTarget];

/** 역할 응답 — 사용자 수 표시 보조(서버 제공 시) */
export interface RoleResponse extends Role {
  /** 배정된 사용자 수(표시용) */
  userCount?: number;
}

/** 권한 응답 */
export type PermissionResponse = Permission;

/** 역할 생성 요청 (POST /roles) */
export interface RoleCreateRequest {
  name: string;
}

/** 역할 수정 요청 (PUT /roles/{id}) */
export interface RoleUpdateRequest {
  name?: string;
}

/** 권한 항목 (저장용) */
export interface PermissionInput {
  targetType: PermissionTarget;
  targetId: string;
  actions: string[];
}

/** 역할 권한 일괄 저장 요청 (PUT /roles/{id}/permissions) */
export interface RolePermissionsUpdateRequest {
  permissions: PermissionInput[];
}

// ---------------------------------------------------------------------------
// 조회
// ---------------------------------------------------------------------------

/** 역할 목록 (회사 단위) */
export function useRoles() {
  return useQuery({
    queryKey: roleKeys.all(),
    queryFn: () => apiFetch<RoleResponse[]>("/api/roles"),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * 역할의 권한 목록 (메뉴/업무/필드 3단).
 * GET /roles/{id}/permissions — 미구현 시 빈 목록 graceful 처리.
 */
export function useRolePermissions(roleId: string | undefined) {
  return useQuery({
    queryKey: roleKeys.permissions(roleId ?? ""),
    enabled: Boolean(roleId),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
    queryFn: () =>
      apiFetch<PermissionResponse[]>(`/api/roles/${encodeURIComponent(roleId ?? "")}/permissions`),
  });
}

/**
 * 사용자(담당자)의 메뉴 권한 목록. GET /users/{id}/permissions — 미구현/빈 시 graceful.
 * 설정>메뉴 권한은 담당자 × 워크스페이스 단위 저장 — workspaceSlug 를 X-Workspace-Slug 로 전달해 현재 워크스페이스 권한만 조회함.
 */
export function useUserPermissions(userId: string | undefined, workspaceSlug?: string) {
  return useQuery({
    queryKey: userPermKeys.permissions(userId ?? "", workspaceSlug),
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
    queryFn: () =>
      apiFetch<PermissionResponse[]>(`/api/users/${encodeURIComponent(userId ?? "")}/permissions`, {
        workspaceSlug,
      }),
  });
}

/**
 * 현재 로그인 사용자 본인의 메뉴 권한 목록. GET /auth/me/permissions — 자가 조회(관리자 아님도 가능).
 * FE 게이팅(사이드바·버튼) 용. 서버 최종 권위는 아이템 CRUD 게이트이며 이 응답은 UX 보조.
 *
 * <p>권한은 워크스페이스 단위이므로 현재 워크스페이스 슬러그를 X-Workspace-Slug 로 전달한다(미전달 시 회사공통).
 */
export function useMyPermissions(workspaceSlug?: string) {
  return useQuery({
    queryKey: ["auth", "me", "permissions", workspaceSlug ?? ""] as const,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
    queryFn: () => apiFetch<PermissionResponse[]>("/api/auth/me/permissions", { workspaceSlug }),
  });
}

// ---------------------------------------------------------------------------
// 뮤테이션
// ---------------------------------------------------------------------------

/** 역할 생성 (POST /roles) */
export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RoleCreateRequest) =>
      apiFetch<RoleResponse>("/api/roles", { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: roleKeys.all() }),
  });
}

/** 역할 수정 (PUT /roles/{id}) */
export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RoleUpdateRequest }) =>
      apiFetch<RoleResponse>(`/api/roles/${encodeURIComponent(id)}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: roleKeys.all() }),
  });
}

/** 역할 삭제 (DELETE /roles/{id}) */
export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/roles/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: roleKeys.all() }),
  });
}

/** 역할 권한 일괄 저장 (PUT /roles/{id}/permissions) */
export function useUpdateRolePermissions(roleId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RolePermissionsUpdateRequest) =>
      apiFetch<PermissionResponse[]>(`/api/roles/${encodeURIComponent(roleId ?? "")}/permissions`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: roleKeys.permissions(roleId ?? "") }),
  });
}
